/**
 * Slip a requested STD (and its inbound / return ferry) so the board stays
 * legal. Quiet hours, turn time, and duty / W&B hard limits stay hard.
 * Does not invent airports or times past the next legal slot.
 */
import {
  MIN_TURN_MIN,
  canAssign,
  estimateRequestEte,
  pickAutoCrewTail,
  projectedTailIcao,
  rankAssignablePairs,
  repositionBetween,
  type AssignTripInput,
  type CrewCandidate,
  type HardRejectCode,
  type RankedPair,
  type TripAssignment,
} from "./assignment";
import { ferryTimesLegal, nextLegalInstant } from "./curfew";
import {
  isLegalConnectedTurn,
  isOpenBoardMission,
  missionStaMs,
  missionStdMs,
  type DispatchBrainInput,
} from "./dispatchBrain";
import { charterForFerry, ferryRole, planFerryDeparture, shiftDependentFerries, type FerryLink } from "./ferryInsert";
import {
  confirmRequestFor,
  newSlipBatchId,
  recordSlip,
  revertSlipMissions,
  type SlipConfirmRequest,
} from "./slipJournal";
import type { AircraftSpec, CharterRequest, Mission, SceneryAirport, SlipCause, SlipJournalEntry } from "./types";

export const SHIFT_STEP_MS = 5 * 60_000;
export const MAX_SHIFT_MS = 18 * 60 * 60_000;
export const SHIFT_PAD_MIN = 20;
const MAX_OVERLAP_PASSES = 16;

const SHIFTABLE: ReadonlySet<HardRejectCode> = new Set([
  "double_booked",
  "crew_double_booked",
  "impossible_reposition",
]);

function asTime(iso: string | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? t : NaN;
}

function tripWindow(depMs: number, eteMin: number, padMin = MIN_TURN_MIN): { start: number; end: number } {
  return {
    start: depMs - padMin * 60_000,
    end: depMs + Math.max(25, eteMin) * 60_000 + padMin * 60_000,
  };
}

function windowsOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start < b.end && b.start < a.end;
}

function isOpenTrip(m: Pick<Mission, "status">): boolean {
  return m.status === "pending" || m.status === "in-progress";
}

function crewIdForMission(assignments: TripAssignment[], missionId: string): string {
  const hit = assignments.find((a) => !a.superseded && a.status !== "declined" && a.missionId === missionId);
  return hit?.crewId ?? "";
}

function conflictsOnBoard(input: {
  depMs: number;
  eteMin: number;
  originIcao: string;
  destIcao: string;
  aircraftId: string;
  crewId: string;
  missions: Mission[];
  assignments: TripAssignment[];
  excludeIds?: ReadonlySet<string>;
}): boolean {
  const mine = {
    originIcao: input.originIcao,
    destIcao: input.destIcao,
    departureIso: new Date(input.depMs).toISOString(),
    eteMin: input.eteMin,
  };
  const window = tripWindow(input.depMs, input.eteMin);
  for (const m of input.missions) {
    if (!isOpenTrip(m)) continue;
    if (input.excludeIds?.has(m.id)) continue;
    const sameTail = m.aircraftId === input.aircraftId;
    const sameCrew = Boolean(input.crewId) && crewIdForMission(input.assignments, m.id) === input.crewId;
    if (!sameTail && !sameCrew) continue;
    if (isLegalConnectedTurn(mine, m)) continue;
    const other = tripWindow(asTime(m.departureIso), m.eteMin);
    if (!Number.isFinite(other.start)) continue;
    if (windowsOverlap(window, other)) return true;
  }
  return false;
}

function earliestAfterPrev(prev: Mission, nextOriginIcao: string, spec: AircraftSpec | undefined, scenery: SceneryAirport[]): number {
  const sta = missionStaMs(prev);
  if (prev.destIcao.toUpperCase() === nextOriginIcao.toUpperCase()) {
    return sta + MIN_TURN_MIN * 60_000;
  }
  if (!spec) return sta + (MIN_TURN_MIN + 45) * 60_000;
  return sta + (repositionBetween(prev.destIcao, nextOriginIcao, spec, scenery).eteMin + MIN_TURN_MIN) * 60_000;
}

function parentCharterOf(missions: Mission[], row: Mission): Mission | null {
  if (row.flightType !== "ferry") return null;
  if (row.parentMissionId) {
    const hit = missions.find((m) => m.id === row.parentMissionId && isOpenBoardMission(m.status));
    if (hit) return hit;
  }
  if (row.inboxRequestId) {
    return (
      missions.find(
        (m) =>
          m.inboxRequestId === row.inboxRequestId &&
          m.flightType !== "ferry" &&
          isOpenBoardMission(m.status),
      ) ?? null
    );
  }
  return null;
}

function rescheduleMission(
  mission: Mission,
  departureIso: string,
  meta: { cause: SlipCause; causeMissionId?: string | null; at: string; batchId: string },
): Mission {
  const slipped = recordSlip(mission, departureIso, meta);
  return {
    ...slipped,
    dispatchWarning: [mission.dispatchWarning, `SCHED  slipped STD to ${departureIso} so the tail / crew stay legal.`]
      .filter(Boolean)
      .join(" · "),
  };
}

export type ShiftedDeparture = {
  ok: true;
  departureIso: string;
  shifted: boolean;
  warning?: string;
};

export type ShiftedDepartureFail = { ok: false; error: string };

/** Next legal STD for this RFQ on this tail / crew — requested time if already legal. */
export function proposeShiftedDeparture(input: {
  request: CharterRequest;
  aircraft: AircraftSpec;
  missions: Mission[];
  assignments: TripAssignment[];
  scenery: SceneryAirport[];
  crewId: string;
  now?: Date;
  currentIcao?: string | null;
  /** Tried once, after the floor, and only if that instant is already legal. */
  preferSlipMin?: number;
}): ShiftedDeparture | ShiftedDepartureFail {
  const now = input.now ?? new Date();
  const requested = asTime(input.request.departureIso);
  if (!Number.isFinite(requested)) return { ok: false, error: "RFQ has no usable STD." };
  const eteMin = estimateRequestEte(input.request, input.aircraft, input.scenery);
  const origin = input.request.originIcao;
  const dest = input.request.destIcao;
  const parked = (input.currentIcao ?? input.aircraft.lastAirportIcao ?? "").toUpperCase() || null;

  const tryAt = (depMs: number): boolean => {
    if (depMs < now.getTime() - 60_000) return false;
    const dep = new Date(depMs);
    if (!ferryTimesLegal(origin, dest, dep, eteMin).legal) return false;
    if (
      conflictsOnBoard({
        depMs,
        eteMin,
        originIcao: origin,
        destIcao: dest,
        aircraftId: input.aircraft.id,
        crewId: input.crewId,
        missions: input.missions,
        assignments: input.assignments,
      })
    ) {
      return false;
    }
    const projected = projectedTailIcao({
      aircraftId: input.aircraft.id,
      missions: input.missions,
      beforeUtc: depMs,
      fallbackIcao: parked,
      ignoreEmptyFerriesFromIcao: origin,
    });
    const repo = repositionBetween(projected, origin, input.aircraft, input.scenery);
    if (repo.eteMin > 0 && projected) {
      let ferryEarliest = now.getTime();
      for (const m of input.missions) {
        if (!isOpenTrip(m) || m.aircraftId !== input.aircraft.id) continue;
        ferryEarliest = Math.max(ferryEarliest, earliestAfterPrev(m, projected, input.aircraft, input.scenery));
      }
      const timed = planFerryDeparture({
        fromIcao: projected,
        toIcao: origin,
        eteMin: repo.eteMin,
        charterStd: dep,
        now,
      });
      const ferryDepMs = Math.max(timed.departure.getTime(), ferryEarliest);
      if (ferryDepMs + repo.eteMin * 60_000 + MIN_TURN_MIN * 60_000 > depMs + 60_000) return false;
      if (
        conflictsOnBoard({
          depMs: ferryDepMs,
          eteMin: repo.eteMin,
          originIcao: projected,
          destIcao: origin,
          aircraftId: input.aircraft.id,
          crewId: input.crewId,
          missions: input.missions,
          assignments: input.assignments,
        })
      ) {
        return false;
      }
    }
    return true;
  };

  if (tryAt(requested)) {
    return { ok: true, departureIso: new Date(requested).toISOString(), shifted: false };
  }

  let floor = Math.max(requested, now.getTime() + SHIFT_PAD_MIN * 60_000);
  for (const m of input.missions) {
    if (!isOpenTrip(m)) continue;
    const sameTail = m.aircraftId === input.aircraft.id;
    const sameCrew = Boolean(input.crewId) && crewIdForMission(input.assignments, m.id) === input.crewId;
    if (!sameTail && !sameCrew) continue;
    const end = earliestAfterPrev(m, origin, input.aircraft, input.scenery);
    if (Number.isFinite(end) && end > floor) floor = end;
  }
  const projectedNow = projectedTailIcao({
    aircraftId: input.aircraft.id,
    missions: input.missions,
    beforeUtc: floor,
    fallbackIcao: parked,
    ignoreEmptyFerriesFromIcao: origin,
  });
  const repoNow = repositionBetween(projectedNow, origin, input.aircraft, input.scenery);
  if (repoNow.eteMin > 0) {
    floor = Math.max(floor, now.getTime() + (repoNow.eteMin + MIN_TURN_MIN + SHIFT_PAD_MIN) * 60_000);
  }

  const ceil = requested + MAX_SHIFT_MS;
  if (input.preferSlipMin && input.preferSlipMin > 0) {
    const want = requested + input.preferSlipMin * 60_000;
    const learnedDep = nextLegalInstant(origin, new Date(Math.max(want, floor))).getTime();
    if (learnedDep - requested <= MAX_SHIFT_MS && tryAt(learnedDep)) {
      const iso = new Date(learnedDep).toISOString();
      return {
        ok: true,
        departureIso: iso,
        shifted: true,
        warning: `SCHED  slipped STD to ${iso} so the tail / crew stay legal (quiet hours and turn respected).`,
      };
    }
  }
  for (let t = floor; t <= ceil; t += SHIFT_STEP_MS) {
    const legalDep = nextLegalInstant(origin, new Date(t)).getTime();
    if (legalDep - requested > MAX_SHIFT_MS) break;
    if (!tryAt(legalDep)) continue;
    const iso = new Date(legalDep).toISOString();
    return {
      ok: true,
      departureIso: iso,
      shifted: true,
      warning: `SCHED  slipped STD to ${iso} so the tail / crew stay legal (quiet hours and turn respected).`,
    };
  }
  return { ok: false, error: "No legal STD within 18 hours that clears the overlap / curfew." };
}

export type ResolvedAssign = {
  pick: RankedPair;
  request: CharterRequest;
  shifted: boolean;
  warning?: string;
};

/** Classic / Grok assign: pick a legal tail×crew, slipping STD when the only block is overlap. */
export function resolveAssignWithShift(
  request: CharterRequest,
  fleet: AircraftSpec[],
  crews: CrewCandidate[],
  rest: Omit<AssignTripInput, "request" | "aircraft" | "crewId" | "crewName" | "duty" | "ratedIcaoTypes">,
  opts?: { learnedRank?: (pair: { aircraftId: string; crewId: string }) => number; preferSlipMin?: number },
): ResolvedAssign | null {
  const direct = pickAutoCrewTail(request, fleet, crews, rest, opts);
  if (direct) {
    return { pick: direct, request, shifted: false };
  }
  const ranked = rankAssignablePairs(request, fleet, crews, rest, opts);
  for (const pair of ranked) {
    const other = pair.hard.filter((h) => !SHIFTABLE.has(h.code) && h.code !== "already_assigned");
    if (other.length) continue;
    const crew = crews.find((c) => c.crewId === pair.crewId);
    if (!crew) continue;
    const shifted = proposeShiftedDeparture({
      request,
      aircraft: pair.aircraft,
      missions: rest.missions,
      assignments: rest.assignments,
      scenery: rest.scenery,
      crewId: pair.crewId,
      now: rest.now,
      currentIcao: pair.currentIcao ?? pair.aircraft.lastAirportIcao,
      preferSlipMin: opts?.preferSlipMin,
    });
    if (!shifted.ok) continue;
    const nextReq = { ...request, departureIso: shifted.departureIso };
    const retry = canAssign({
      ...rest,
      request: nextReq,
      aircraft: pair.aircraft,
      crewId: pair.crewId,
      crewName: pair.crewName,
      duty: crew.duty,
      ratedIcaoTypes: crew.ratedIcaoTypes,
    });
    if (!retry.ok) continue;
    return {
      pick: { ...pair, ...retry, aircraft: pair.aircraft },
      request: nextReq,
      shifted: shifted.shifted,
      warning: shifted.warning,
    };
  }
  return null;
}

function slipOpenTrip(
  missions: Mission[],
  row: Mission,
  departureIso: string,
  assignments: readonly FerryLink[] | undefined,
  meta: { at: string; batchId: string; now: Date },
  blocked: SlipConfirmRequest[],
): Mission[] {
  const held = confirmRequestFor(row, departureIso);
  if (held) {
    blocked.push(held);
    return missions;
  }
  const next = rescheduleMission(row, departureIso, {
    cause: "auto_resolve",
    causeMissionId: null,
    at: meta.at,
    batchId: meta.batchId,
  });
  const replaced = missions.map((m) => (m.id === row.id ? next : m));
  const ferryBlocked: SlipConfirmRequest[] = [];
  const shifted = (rows: Mission[], charter: Mission, ferryOpts?: { afterMs?: number }) =>
    shiftDependentFerries(rows, charter, meta.now, {
      ...ferryOpts,
      confirmed: false,
      batchId: meta.batchId,
      cause: "ferry",
      causeMissionId: charter.id,
      at: meta.at,
      onBlocked: (request) => ferryBlocked.push(request),
    });
  if (row.flightType !== "ferry") {
    const out = shifted(replaced, next);
    if (ferryBlocked.length) {
      blocked.push(...ferryBlocked);
      return missions;
    }
    return out;
  }
  // A positioning ferry has to be down before its charter, so the charter may
  // move later. A return (home) ferry flies after the charter — slipping it
  // must not drag the parent, or the pax leg jumps past the time that was asked.
  const role = ferryRole(missions, row, assignments);
  if (role === "positioning") {
    const parent = charterForFerry(missions, row, assignments) ?? parentCharterOf(missions, row);
    if (!parent) return replaced;
    const linkedStd =
      new Date(departureIso).getTime() + Math.max(25, row.eteMin) * 60_000 + MIN_TURN_MIN * 60_000;
    const parentStd = Math.max(missionStdMs(parent), linkedStd);
    const parentIso = new Date(parentStd).toISOString();
    const parentHeld = confirmRequestFor(parent, parentIso, row.id);
    if (parentHeld) {
      blocked.push(parentHeld);
      return missions;
    }
    const slipped = rescheduleMission(parent, parentIso, {
      cause: "auto_resolve",
      causeMissionId: row.id,
      at: meta.at,
      batchId: meta.batchId,
    });
    const out = shifted(
      replaced.map((m) => (m.id === parent.id ? slipped : m)),
      slipped,
    );
    if (ferryBlocked.length) {
      blocked.push(...ferryBlocked);
      return missions;
    }
    return out;
  }
  const slippedAt = new Date(departureIso).getTime();
  const out = shifted(replaced, next, { afterMs: slippedAt });
  if (ferryBlocked.length) {
    blocked.push(...ferryBlocked);
    return missions;
  }
  return out;
}

/**
 * Walk each tail in STD order and slip the later sector (plus its charter /
 * ferries) until company turn fits. Time-shift only — does not reassign tails
 * or cancel tickets.
 */
export function resolveBoardOverlaps(input: DispatchBrainInput): {
  missions: Mission[];
  assignments: TripAssignment[];
  shifted: boolean;
  slips: SlipJournalEntry[];
  needsConfirm: SlipConfirmRequest[];
} {
  let missions = input.missions.map((m) => ({ ...m, legs: m.legs ? m.legs.map((l) => ({ ...l })) : m.legs }));
  let shifted = false;
  const now = input.now ?? new Date();
  const batchId = newSlipBatchId(now);
  const needsConfirm: SlipConfirmRequest[] = [];
  const originalStd = new Map(input.missions.map((m) => [m.id, missionStdMs(m)]));
  const slipped = (m: Mission) => (missionStdMs(m) !== originalStd.get(m.id) ? 1 : 0);

  for (let pass = 0; pass < MAX_OVERLAP_PASSES; pass++) {
    // On an STD tie a leg that was not slipped here keeps its slot; ids are random, so never let them decide.
    const open = missions.filter((m) => isOpenBoardMission(m.status)).sort((a, b) => {
      const std = missionStdMs(a) - missionStdMs(b);
      if (std !== 0) return std;
      const moved = slipped(a) - slipped(b);
      if (moved !== 0) return moved;
      const booked = (originalStd.get(a.id) ?? 0) - (originalStd.get(b.id) ?? 0);
      return booked !== 0 ? booked : a.id.localeCompare(b.id);
    });
    const byTail = new Map<string, Mission[]>();
    for (const row of open) {
      if (!row.aircraftId) continue;
      const list = byTail.get(row.aircraftId) ?? [];
      list.push(row);
      byTail.set(row.aircraftId, list);
    }
    let changed = false;
    for (const [aircraftId, list] of byTail) {
      const spec = input.fleet.find((a) => a.id === aircraftId);
      for (let i = 0; i < list.length - 1; i++) {
        const prev = missions.find((m) => m.id === list[i]!.id) ?? list[i]!;
        const next = missions.find((m) => m.id === list[i + 1]!.id) ?? list[i + 1]!;
        if (!isOpenBoardMission(prev.status) || !isOpenBoardMission(next.status)) continue;
        // The charter is never the leg we slip when the overlap is its own
        // home ferry sitting on top of it. Move the ferry to after the charter.
        let anchor = prev;
        let slip = next;
        if (
          next.flightType !== "ferry" &&
          prev.flightType === "ferry" &&
          ferryRole(missions, prev, input.assignments) === "return"
        ) {
          const parent = charterForFerry(missions, prev, input.assignments);
          if (parent?.id === next.id) {
            anchor = next;
            slip = prev;
          }
        }
        const earliest = earliestAfterPrev(anchor, slip.originIcao, spec, input.scenery);
        const legal = nextLegalInstant(slip.originIcao, new Date(Math.max(earliest, now.getTime()))).getTime();
        if (missionStdMs(slip) + 1000 >= legal) continue;
        // A curfew jump bigger than the shift cap stays a clash for a hand move.
        if (legal - missionStdMs(slip) > MAX_SHIFT_MS) continue;
        const iso = new Date(legal).toISOString();
        const before = missions;
        const blocked: SlipConfirmRequest[] = [];
        missions = slipOpenTrip(missions, slip, iso, input.assignments, { at: now.toISOString(), batchId, now }, blocked);
        if (blocked.length) {
          needsConfirm.push(...blocked);
          missions = before;
          continue;
        }
        shifted = true;
        changed = true;
        break;
      }
      if (changed) break;
    }
    if (!changed) break;
  }

  const slips = missions.flatMap((mission) => (mission.slips ?? []).filter((row) => row.batchId === batchId));
  return { missions, assignments: input.assignments, shifted, slips, needsConfirm };
}

/** Desk edit of one STD. A move past 24 hours or onto another duty day waits for confirm. */
export function applyManualDeparture(input: {
  missions: Mission[];
  missionId: string;
  departureIso: string;
  now?: Date;
  confirmed?: boolean;
}): { missions: Mission[]; applied: boolean; needsConfirm: SlipConfirmRequest | null } {
  const now = input.now ?? new Date();
  const target = input.missions.find((row) => row.id === input.missionId);
  if (!target) return { missions: input.missions, applied: false, needsConfirm: null };
  const iso = new Date(input.departureIso).toISOString();
  const held = input.confirmed ? null : confirmRequestFor(target, iso);
  if (held) return { missions: input.missions, applied: false, needsConfirm: held };
  const at = now.toISOString();
  const batchId = newSlipBatchId(now);
  const next = recordSlip(target, iso, { cause: "desk", at, batchId });
  const replaced = input.missions.map((row) => (row.id === target.id ? next : row));
  const ferryBlocked: SlipConfirmRequest[] = [];
  const shifted = shiftDependentFerries(replaced, next, now, {
    confirmed: Boolean(input.confirmed),
    batchId,
    cause: "ferry",
    causeMissionId: target.id,
    at,
    onBlocked: (request) => ferryBlocked.push(request),
  });
  if (ferryBlocked.length && !input.confirmed) {
    return { missions: input.missions, applied: false, needsConfirm: ferryBlocked[0] ?? null };
  }
  return { missions: shifted, applied: true, needsConfirm: null };
}

export function revertSlips(missions: readonly Mission[], missionId: string, now = new Date()) {
  return revertSlipMissions(missions, missionId, now);
}
